import type { PdpEcommerceBlockProductFocusSideSlideProps } from '@design-system/components/Pdp/EcommerceBlock/PdpEcommerceBlockProductFocusSideSlide.props'

/**
 * Composable for fetching product focus items from CMS.
 *
 * @param {string[]} deliveryKeys - Array of delivery keys to fetch product focus items.
 * @returns {Promise<Object>} An object containing fetched product focus data.
 * @property {PdpEcommerceBlockProductFocusSideSlideProps} data - Product focus items with images and descriptions.
 *
 * @example
 * const { data } = await useProductFocus(['key1', 'key2'])
 *
 * @remarks
 * This async composable fetches product focus items from the CMS API.
 * It supports multiple delivery keys and handles light/dark mode images.
 */
export const useProductFocus = async (deliveryKeys: string[]) => {
  const nuxtApp = useNuxtApp()

  const localePath = await nuxtApp.runWithContext(() => useLocalePath())
  const route = await nuxtApp.runWithContext(() => useRoute())

  /**
   * Fetches product focus items using async data.
   *
   * @type {AsyncData<PdpEcommerceBlockProductFocusSideSlideProps>}
   */
  const { data } = await nuxtApp.runWithContext(
    async () =>
      await useAsyncData<PdpEcommerceBlockProductFocusSideSlideProps>(
        `product-focus-${deliveryKeys.join('_')}`,
        async () => {
          /**
           * Fetch product focus items for each delivery key.
           *
           * @type {Array<Object>}
           */
          const items = await Promise.all(
            deliveryKeys.map(key =>
              $fetch('/api/cms/getProductFocusItem', {
                query: {
                  // @ts-ignore
                  locale: localePath(''),
                  // @ts-ignore
                  vse: route.query.vse,
                  // @ts-ignore
                  contentId: route.query.contentId,
                  deliveryKey: key.replaceAll(' ', '_'),
                },
              })
            )
          )

          /**
           * Transform fetched items into a standardized format.
           *
           * @returns {PdpEcommerceBlockProductFocusSideSlideProps}
           */
          return {
            items: items.map(item => ({
              image: {
                light: item?.content?.light?.icon?.secure_url ?? '',
                dark: item?.content?.dark?.icon?.secure_url ?? '',
                alt: item?.content.alt ?? '',
              },
              description: item?.content.description ?? '',
            })),
          }
        }
      )
  )

  return { data }
}
